import * as R from 'remeda';
import {
  endOfMonth,
  endOfWeek,
  getMonth,
  getWeek,
  getWeekYear,
  getYear,
  Locale,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { IntlShape, ResolvedIntlConfig } from 'react-intl';
import {
  DocumentAutomationTimeSeriesData,
  EstimatedErrorRateTimeseriesData,
} from '@rossum/api-client/queues';
import { dateFnsLocales } from '../../../../i18n/dateLocales';
import { convertFromUTC } from '../../../document-list-filtering/dateUtils';
import { AggregationPeriod, BlockedDocumentKeys } from './types';

export const getPeriodBoundaries = <T extends { date: string }>(data: T[]) => ({
  startDate:
    R.firstBy(data, item => new Date(item.date).getTime())?.date ?? null,
  endDate:
    R.firstBy(data, item => -new Date(item.date).getTime())?.date ?? null,
});

export const getEndOfPeriod = ({
  aggregationPeriod,
  referenceDate,
  locale,
}: {
  aggregationPeriod: AggregationPeriod;
  referenceDate: Date;
  locale?: Locale;
}) => {
  return aggregationPeriod === 'months'
    ? endOfMonth(referenceDate)
    : endOfWeek(referenceDate, { locale });
};

export const aggregateTimeseries = <T extends { date: string }>(
  timeseries: T[],
  {
    aggregationPeriod,
    reducerCallback,
    groupBy,
    locale,
  }: {
    aggregationPeriod: AggregationPeriod;
    reducerCallback: (params: {
      date: string;
      prev: T;
      curr: T;
      array: T[];
    }) => T;
    locale?: Locale;
    groupBy?: (period: string, item: T) => string;
  }
) => {
  const localeOptions = locale ? { locale } : undefined;

  const groupedByPeriodNo = R.groupBy(timeseries, item => {
    const d = parseISO(item.date);
    const year =
      aggregationPeriod === 'months'
        ? getYear(d)
        : getWeekYear(d, localeOptions);
    const period =
      aggregationPeriod === 'months' ? getMonth(d) : getWeek(d, localeOptions);
    const groupKey = `${year}-${period}`;
    return groupBy?.(groupKey, item) ?? groupKey;
  });

  return R.pipe(
    groupedByPeriodNo,
    R.mapValues(([first, ...rest]) => {
      const periodStartDate = convertFromUTC(
        aggregationPeriod === 'months'
          ? startOfMonth(parseISO(first.date))
          : startOfWeek(parseISO(first.date), localeOptions)
      ).toISOString();

      return rest.reduce<T>(
        (prev, curr) =>
          reducerCallback({
            date: periodStartDate,
            prev,
            curr,
            array: [first, ...rest],
          }),
        { ...first, date: periodStartDate }
      );
    }),
    R.values(),
    R.sortBy(seriesItem => new Date(seriesItem.date))
  );
};

type GetAggregatedAutomationRatesParams = {
  documentAutomationTimeseries: DocumentAutomationTimeSeriesData[];
  locale: Locale;
  aggregationPeriod: AggregationPeriod;
};
export const getAggregatedAutomationRates = ({
  aggregationPeriod,
  documentAutomationTimeseries,
  locale,
}: GetAggregatedAutomationRatesParams) => {
  return aggregateTimeseries(documentAutomationTimeseries, {
    aggregationPeriod,
    locale,
    reducerCallback: ({ date, prev, curr }) => ({
      date,
      automatedCount: prev.automatedCount + curr.automatedCount,
      nonAutomatedCount: prev.nonAutomatedCount + curr.nonAutomatedCount,
      touchedCount: prev.touchedCount + curr.touchedCount,
      touchlessCount: prev.touchlessCount + curr.touchlessCount,
    }),
  });
};

type GetAggregatedErrorRatesParams = {
  estimatedErrorRateTimeseries: EstimatedErrorRateTimeseriesData[];
  locale: Locale;
  aggregationPeriod: AggregationPeriod;
};

/**
 * Sums two weighted error rate estimates, preserving `null` when neither side
 * has an estimate at all.
 */
const addEstimates = (a: number | null, b: number | null) =>
  a === null && b === null ? null : (a ?? 0) + (b ?? 0);

export const getAggregatedErrorRates = ({
  estimatedErrorRateTimeseries,
  aggregationPeriod,
  locale,
}: GetAggregatedErrorRatesParams) => {
  return R.pipe(
    // days without an error rate estimate (`null`) are excluded from both the
    // weighted sum and its denominator, so they neither pull the period's rate
    // towards zero nor count as documents.
    estimatedErrorRateTimeseries.map(item =>
      item.errorRateEstimate === null
        ? { ...item, errorRateEstimate: null, windowDocumentCount: 0 }
        : {
            ...item,
            errorRateEstimate:
              item.errorRateEstimate * item.windowDocumentCount,
          }
    ),
    weightedSeries =>
      aggregateTimeseries(weightedSeries, {
        aggregationPeriod,
        locale,
        reducerCallback: ({ date, prev, curr, array }) => ({
          date,
          errorRateEstimate: addEstimates(
            prev.errorRateEstimate,
            curr.errorRateEstimate
          ),
          windowDocumentCount:
            prev.windowDocumentCount + curr.windowDocumentCount,
          // if majority of the estimates are rough, then we will display the line chart as rough for the aggregated period
          isQualityEstimate: R.pipe(
            array,
            R.partition(data => data.isQualityEstimate),
            ([precisionSeries, roughSeries]) =>
              precisionSeries.length > roughSeries.length
          ),
        }),
      }),
    R.map(item => ({
      date: item.date,
      isQualityEstimate: item.isQualityEstimate,
      // a period where no day had an estimate has no error rate of its own
      errorRateEstimate:
        item.errorRateEstimate !== null && item.windowDocumentCount > 0
          ? item.errorRateEstimate / item.windowDocumentCount
          : null,
    }))
  );
};

const blockedDocumentKeyMap = {
  low_score: 'lowScore',
  failed_checks: 'failedChecks',
  no_validation_sources: 'noValidationSources',
  error_message: 'errorMessage',
  human_confirmation_required: 'humanConfirmationRequired',
  delete_recommendation_filename: 'deleteRecommendationFilename',
  delete_recommendation_page_count: 'deleteRecommendationPageCount',
  automation_disabled: 'automationDisabled',
  delete_recommendation_field: 'deleteRecommendationField',
  is_duplicate: 'isDuplicate',
  suggested_edit_present: 'suggestedEditPresent',
  extension: 'extension',
} as const satisfies Record<BlockedDocumentKeys, string>;

const isBlockedDocumentKey = (key: string): key is BlockedDocumentKeys =>
  key in blockedDocumentKeyMap;

export const getBlockerTranslation = ({
  intl,
  key,
}: {
  intl: IntlShape;
  key: string;
}) => {
  if (!isBlockedDocumentKey(key)) return key;
  return intl.formatMessage({
    id: `containers.settings.queues.queue.automation.blockedDocumentCounts.${blockedDocumentKeyMap[key]}`,
  });
};

export const getDetailPanelHeight = () => 'auto' as const;

// A precaution to limit the size of the browser url
export const getLimitedAnnotationIds = (blockedDocumentIds: number[]) =>
  R.pipe(blockedDocumentIds, R.take(50));

export const getDateLocale = (intlLocale: ResolvedIntlConfig['locale']) => {
  const isUSLocale = navigator.language === 'en-US';
  return isUSLocale
    ? dateFnsLocales.enUS
    : (dateFnsLocales[intlLocale] ?? dateFnsLocales.en);
};
